/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import { useState, useMemo } from 'react';
import { Modal, Button, Checkbox, Typography, Spin } from 'antd';
import {
  PrinterOutlined,
  SortAscendingOutlined,
  SortDescendingOutlined,
} from '@ant-design/icons';
import { useIntl } from 'react-intl';
import { DbQuestion } from '~src/types/db';
import QATable from './QATable';
import { useQACopy } from './useQACopy';
import { filterAndSortQuestions } from './utils';
import { useQuestions } from '~src/hooks/api-hooks/useQuestions';
import { useSharedTenderQuestions } from '~src/hooks/api-hooks/useSharedTenderQuestions';
import { useSharedTenderMyQuestions } from '~src/hooks/api-hooks/useSharedTendersMyQuestions';

type CopyQAModalProps = {
  visible: boolean;
  onCancel: () => void;
  questions: DbQuestion[];
  isVendorSide: boolean;
  projectId?: number;
  tenderId: string;
  shareId?: string;
  shareKey?: string;
  vendorQuestionType?: 'all' | 'my';
};

const CopyQAModal: React.FC<CopyQAModalProps> = ({
  visible,
  onCancel,
  questions,
  isVendorSide,
  projectId,
  tenderId,
  shareId,
  shareKey,
  vendorQuestionType,
}) => {
  const intl = useIntl();
  const [anonymousMode, setAnonymousMode] = useState(true);
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');

  const { handleCopyToClipboard, isLoading } = useQACopy();

  // Fetch all questions (without pagination) based on side
  const projectQuestionsQuery = useQuestions(
    {
      projectId,
      tenderId,
      skip: 0,
      take: 999999,
      showRejected: true,
    },
    {
      enabled: visible && !isVendorSide && !!projectId && !!tenderId,
    }
  );

  const vendorAllQuestionsQuery = useSharedTenderQuestions(
    {
      share_id: shareId!,
      share_key: shareKey!,
      skip: 0,
      take: 999999,
    },
    {
      enabled:
        visible &&
        isVendorSide &&
        vendorQuestionType === 'all' &&
        !!shareId &&
        !!shareKey,
    }
  );

  const vendorMyQuestionsQuery = useSharedTenderMyQuestions(
    {
      share_id: shareId!,
      share_key: shareKey!,
      skip: 0,
      take: 999999,
    },
    {
      enabled:
        visible &&
        isVendorSide &&
        vendorQuestionType === 'my' &&
        !!shareId &&
        !!shareKey,
    }
  );

  // Determine which data to use
  const allQuestionsData = useMemo(() => {
    if (!isVendorSide) {
      return projectQuestionsQuery.data?.questions || [];
    } else {
      if (vendorQuestionType === 'my') {
        return vendorMyQuestionsQuery.data?.items || [];
      } else {
        return vendorAllQuestionsQuery.data?.items || [];
      }
    }
  }, [
    isVendorSide,
    vendorQuestionType,
    projectQuestionsQuery.data,
    vendorAllQuestionsQuery.data,
    vendorMyQuestionsQuery.data,
  ]);

  // Loading state for queries
  const isLoadingQuestions =
    projectQuestionsQuery.isFetching ||
    vendorAllQuestionsQuery.isFetching ||
    vendorMyQuestionsQuery.isFetching;

  // Filter and sort questions using fetched data
  const processedQuestions = useMemo(() => {
    return filterAndSortQuestions(allQuestionsData, isVendorSide, sortOrder);
  }, [allQuestionsData, isVendorSide, sortOrder]);

  const handleCopy = async () => {
    await handleCopyToClipboard(
      processedQuestions,
      anonymousMode,
      isVendorSide
    );
  };

  const handlePrint = () => {
    window.print();
  };

  const toggleSortOrder = () => {
    setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
  };

  return (
    <Modal
      visible={visible}
      onCancel={onCancel}
      width={1200}
      title={intl.formatMessage({ id: 'qa-copy-modal-title' })}
      footer={null}
      bodyStyle={{
        padding: 0,
        maxHeight: '70vh',
        display: 'flex',
        flexDirection: 'column',
      }}
      destroyOnClose>
      {/* Scrollable content area */}
      <div
        className="qa-modal-content"
        style={{
          flex: 1,
          overflowY: 'auto',
          padding: '20px 24px',
          minHeight: 0,
        }}>
        {isLoadingQuestions ? (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: 200,
            }}>
            <Spin size="large" />
          </div>
        ) : processedQuestions.length > 0 ? (
          <QATable
            questions={processedQuestions}
            anonymousMode={anonymousMode}
            isVendorSide={isVendorSide}
          />
        ) : (
          <div
            style={{
              padding: 40,
              textAlign: 'center',
              color: '#999',
            }}>
            <Typography.Text>
              {intl.formatMessage({ id: 'qa-copy-modal-empty' })}
            </Typography.Text>
          </div>
        )}
      </div>

      {/* Sticky footer */}
      <div
        className="qa-modal-footer"
        style={{
          borderTop: '1px solid #f0f0f0',
          padding: '16px 24px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          background: '#fff',
        }}>
        {/* Left: Anonymous mode checkbox (only for project side) */}
        <div style={{ flex: 1 }}>
          {!isVendorSide && (
            <Checkbox
              checked={anonymousMode}
              onChange={(e) => setAnonymousMode(e.target.checked)}>
              {intl.formatMessage({ id: 'qa-copy-anonymous-mode' })}
            </Checkbox>
          )}
        </div>

        {/* Center: Copy and Print buttons */}
        <div
          style={{
            flex: 1,
            display: 'flex',
            justifyContent: 'center',
            gap: 8,
          }}>
          <Button
            type="primary"
            onClick={handleCopy}
            loading={isLoading}
            disabled={isLoadingQuestions || processedQuestions.length === 0}
            size="large">
            {intl.formatMessage({ id: 'qa-copy-to-clipboard-new' })}
          </Button>
          <Button
            icon={<PrinterOutlined rev={''} />}
            onClick={handlePrint}
            disabled={isLoadingQuestions || processedQuestions.length === 0}
            size="large">
            {intl.formatMessage({ id: 'qa-copy-print' })}
          </Button>
        </div>

        {/* Right: Sort by date toggle */}
        <div
          style={{
            flex: 1,
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
          }}>
          <Typography.Text style={{ marginRight: 8 }}>
            {intl.formatMessage({ id: 'qa-copy-sort-by-date' })}:
          </Typography.Text>
          <Button
            icon={
              sortOrder === 'asc' ? (
                <SortAscendingOutlined rev={''} />
              ) : (
                <SortDescendingOutlined rev={''} />
              )
            }
            onClick={toggleSortOrder}
          />
        </div>
      </div>

      {/* Print-specific styles */}
      <style jsx global>{`
        @media print {
          /* Hide everything by default */
          body * {
            visibility: hidden !important;
          }

          /* Show only the modal and its contents */
          .ant-modal,
          .ant-modal *,
          .qa-modal-content,
          .qa-modal-content * {
            visibility: visible !important;
          }

          /* Hide modal chrome */
          .ant-modal-mask,
          .ant-modal-wrap > .ant-modal > .ant-modal-content > .ant-modal-close,
          .ant-modal-header,
          .ant-modal-title,
          .qa-modal-footer {
            display: none !important;
            visibility: hidden !important;
          }

          /* Position modal content for print */
          .ant-modal-wrap {
            position: absolute;
            top: 0;
            left: 0;
            overflow: visible !important;
          }

          .ant-modal {
            position: absolute !important;
            top: 0 !important;
            left: 0 !important;
            margin: 0 !important;
            padding: 0 !important;
            max-width: 100% !important;
            width: 100% !important;
            transform: none !important;
          }

          .ant-modal-content {
            box-shadow: none !important;
            border: none !important;
            border-radius: 0 !important;
          }

          .ant-modal-body {
            padding: 0 !important;
            max-height: none !important;
            overflow: visible !important;
          }

          .qa-modal-content {
            padding: 20px !important;
            overflow: visible !important;
            max-height: none !important;
          }

          /* Table print styles */
          .ant-table {
            page-break-inside: auto;
            width: 100%;
          }

          .ant-table-thead {
            display: table-header-group;
          }

          .ant-table-thead > tr > th {
            background: #f0f0f0 !important;
            font-weight: bold;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }

          .ant-table tr {
            page-break-inside: avoid;
            page-break-after: auto;
          }

          .ant-table td,
          .ant-table th {
            border: 1px solid #d9d9d9 !important;
          }

          /* Ensure zebra striping in print */
          .qa-table-row-even {
            background-color: #fafafa !important;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }

          .qa-table-row-odd {
            background-color: #ffffff !important;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
        }
      `}</style>
    </Modal>
  );
};

export default CopyQAModal;
